/**
 * Notification Trigger Variables Reference
 *
 * This file contains the mapping of notification triggers to their available variables.
 * These variables can be used in notification templates using {{variable_name}} syntax.
 *
 * @see /Users/ahmadabdalla/Projects/quantix-api/docs/NOTIFICATION_VARIABLES_REFERENCE.md
 */

export interface TriggerVariable {
    name: string;
    description: string;
    example?: string;
}

export interface TriggerInfo {
    category: string;
    description: string;
    variables: TriggerVariable[];
}

/**
 * General variables available to ALL notification templates
 */
export const GENERAL_VARIABLES: TriggerVariable[] = [
    {
        name: 'system_name',
        description: "The tenant's system name",
        example: 'Quantix Trading Platform',
    },
    {
        name: 'company_name',
        description: "The tenant's company name (alias for system_name)",
        example: 'Quantix Trading Platform',
    },
    {
        name: 'system_logo_url',
        description: "URL to the tenant's logo (if uploaded)",
        example: 'https://example.com/logo.png',
    },
    {
        name: 'support_email',
        description: 'Support email address',
        example: 'support@quantix.com',
    },
    {
        name: 'support_phone',
        description: 'Support phone number',
        example: '+1234567890',
    },
    {
        name: 'cta_url',
        description: 'Call-to-action URL (login page)',
        example: 'https://quantix.com/login',
    },
    {
        name: 'current_year',
        description: 'Current year',
        example: '2026',
    },
    {
        name: 'current_date',
        description: 'Current date (formatted per tenant locale)',
        example: '2026-02-09',
    },
    {
        name: 'current_time',
        description: 'Current time (formatted per tenant locale)',
        example: '14:30:00',
    },
    {
        name: 'current_datetime',
        description: 'Current date and time (formatted per tenant locale)',
        example: '2026-02-09 14:30:00',
    },
    {
        name: 'timestamp',
        description: 'Current timestamp (ISO 8601 format)',
        example: '2026-02-09T14:30:00+00:00',
    },
];

/**
 * Trigger-specific variables mapping
 */
export const TRIGGER_VARIABLES: Record<string, TriggerInfo> = {
    // Customer Lifecycle Triggers
    customer_registered: {
        category: 'Customer Lifecycle',
        description: 'When a customer completes registration',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'account_type', description: 'Account type' },
            { name: 'registration_date', description: 'Registration date/time' },
        ],
    },
    customer_created_by_crm: {
        category: 'Customer Lifecycle',
        description:
            'When a CRM user creates a customer (from scratch or by converting a lead). Not for self-registration.',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'created_by', description: 'Name of CRM user who created the customer' },
            { name: 'created_by_id', description: 'ID of CRM user who created the customer' },
            { name: 'timestamp', description: 'When the customer was created' },
            {
                name: 'assigned_password',
                description:
                    'Auto-generated password for the customer; use in template to send login credentials',
            },
            { name: 'user_name', description: 'Name of the CRM user who performed this action' },
            { name: 'user_email', description: 'Email of the CRM user who performed this action' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the CRM user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the CRM user' },
        ],
    },
    customer_updated_by_crm: {
        category: 'Customer Lifecycle',
        description: 'When a CRM user updates a customer',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'updated_by', description: 'Name of CRM user who updated the customer' },
            { name: 'updated_by_id', description: 'ID of CRM user who updated the customer' },
            { name: 'timestamp', description: 'When the customer was updated' },
            { name: 'user_name', description: 'Name of the CRM user who performed this action' },
            { name: 'user_email', description: 'Email of the CRM user who performed this action' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the CRM user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the CRM user' },
        ],
    },
    customer_deleted_by_crm: {
        category: 'Customer Lifecycle',
        description: 'When a CRM user deletes a customer',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'deleted_by', description: 'Name of CRM user who deleted the customer' },
            { name: 'deleted_by_id', description: 'ID of CRM user who deleted the customer' },
            { name: 'timestamp', description: 'When the customer was deleted' },
            { name: 'user_name', description: 'Name of the CRM user who performed this action' },
            { name: 'user_email', description: 'Email of the CRM user who performed this action' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the CRM user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the CRM user' },
        ],
    },

    // Trading Triggers
    trade_opened: {
        category: 'Trading',
        description: 'When a trade position is opened (market order executed)',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'trade_id', description: 'Trade UUID' },
            { name: 'instrument', description: 'Trading instrument', example: 'EUR/USD' },
            { name: 'volume', description: 'Trade volume/lot size' },
            { name: 'price', description: 'Entry price' },
            { name: 'side', description: 'Trade side (buy/sell)' },
            { name: 'trade_value', description: 'Total trade value' },
            { name: 'margin_level', description: 'Current margin level' },
        ],
    },
    trade_closed: {
        category: 'Trading',
        description: 'When a trade position is closed',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'trade_id', description: 'Trade UUID' },
            { name: 'instrument', description: 'Trading instrument' },
            { name: 'profit_loss', description: 'Profit or loss amount' },
            { name: 'duration', description: 'How long the trade was open' },
        ],
    },
    order_opened: {
        category: 'Trading',
        description: 'When a pending order (limit/stop) is placed',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'order_id', description: 'Order UUID' },
            { name: 'trade_id', description: 'Trade UUID' },
            { name: 'instrument', description: 'Trading instrument' },
            { name: 'order_type', description: 'Order type (limit/stop)' },
            { name: 'side', description: 'Order side (buy/sell)' },
            { name: 'lot_size', description: 'Order lot size' },
            { name: 'entry_price', description: 'Order entry price' },
        ],
    },
    large_position: {
        category: 'Trading',
        description: 'When a large trading position is detected',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'trade_id', description: 'Trade UUID' },
            { name: 'instrument', description: 'Trading instrument' },
            { name: 'trade_value', description: 'Total trade value' },
            { name: 'volume', description: 'Trade volume/lot size' },
            { name: 'price', description: 'Entry price' },
            { name: 'side', description: 'Trade side (buy/sell)' },
        ],
    },
    margin_call: {
        category: 'Trading',
        description: 'When a margin call is triggered',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'margin_level', description: 'Current margin level' },
            { name: 'trade_id', description: 'Trade UUID' },
            { name: 'instrument', description: 'Trading instrument' },
        ],
    },

    // Financial Triggers
    deposit_requested: {
        category: 'Financial',
        description: 'When a deposit is requested/initiated',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'transaction_id', description: 'Transaction UUID' },
            { name: 'amount', description: 'Deposit amount' },
            { name: 'currency', description: 'Currency code' },
            { name: 'payment_method', description: 'Payment method used' },
            { name: 'transaction_type', description: 'Transaction type' },
        ],
    },
    deposit_received: {
        category: 'Financial',
        description: 'When a deposit is confirmed/completed',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'transaction_id', description: 'Transaction UUID' },
            { name: 'amount', description: 'Deposit amount' },
            { name: 'currency', description: 'Currency code' },
            { name: 'payment_method', description: 'Payment method used' },
            { name: 'transaction_type', description: 'Transaction type' },
        ],
    },
    bonus_received: {
        category: 'Financial',
        description: 'When a bonus is credited to account',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'transaction_id', description: 'Transaction UUID' },
            { name: 'amount', description: 'Bonus amount' },
            { name: 'currency', description: 'Currency code' },
            { name: 'transaction_type', description: 'Transaction type (bonus)' },
        ],
    },
    withdrawal_requested: {
        category: 'Financial',
        description: 'When a withdrawal is requested',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'transaction_id', description: 'Transaction UUID' },
            { name: 'amount', description: 'Withdrawal amount' },
            { name: 'currency', description: 'Currency code' },
            { name: 'payment_method', description: 'Payment method used' },
            { name: 'transaction_type', description: 'Transaction type' },
        ],
    },
    withdrawal_completed: {
        category: 'Financial',
        description: 'When a withdrawal is approved/completed',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'transaction_id', description: 'Transaction UUID' },
            { name: 'amount', description: 'Withdrawal amount' },
            { name: 'currency', description: 'Currency code' },
            { name: 'payment_method', description: 'Payment method used' },
            { name: 'transaction_type', description: 'Transaction type' },
        ],
    },
    high_risk_transaction: {
        category: 'Financial',
        description: 'When a high-risk transaction occurs',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'transaction_id', description: 'Transaction UUID' },
            { name: 'amount', description: 'Transaction amount' },
            { name: 'currency', description: 'Currency code' },
            { name: 'transaction_type', description: 'Transaction type' },
        ],
    },

    // Account Management Triggers
    account_created_by_crm: {
        category: 'Account Management',
        description: 'When a CRM user creates a trading account',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'account_id', description: 'Account UUID' },
            { name: 'account_number', description: 'Account readable number' },
            { name: 'created_by', description: 'Name of CRM user' },
            { name: 'created_by_id', description: 'ID of CRM user' },
            { name: 'user_name', description: 'Name of the CRM user who performed this action' },
            { name: 'user_email', description: 'Email of the CRM user who performed this action' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the CRM user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the CRM user' },
        ],
    },
    account_updated_by_crm: {
        category: 'Account Management',
        description: 'When a CRM user updates a trading account',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'account_id', description: 'Account UUID' },
            { name: 'account_number', description: 'Account readable number' },
            { name: 'updated_by', description: 'Name of CRM user' },
            { name: 'updated_by_id', description: 'ID of CRM user' },
            { name: 'user_name', description: 'Name of the CRM user who performed this action' },
            { name: 'user_email', description: 'Email of the CRM user who performed this action' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the CRM user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the CRM user' },
        ],
    },
    account_locked: {
        category: 'Account Management',
        description: 'When an account is locked',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'account_id', description: 'Account UUID' },
            { name: 'account_number', description: 'Account readable number' },
            { name: 'reason', description: 'Reason for locking' },
            { name: 'locked_by', description: 'Who locked the account' },
        ],
    },
    account_suspended: {
        category: 'Account Management',
        description: 'When an account is suspended',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'account_id', description: 'Account UUID' },
            { name: 'account_number', description: 'Account readable number' },
            { name: 'reason', description: 'Reason for suspension' },
            { name: 'suspended_by', description: 'Who suspended the account' },
        ],
    },
    account_activated: {
        category: 'Account Management',
        description: 'When an account is activated',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'account_id', description: 'Account UUID' },
            { name: 'account_number', description: 'Account readable number' },
            { name: 'activated_by', description: 'Who activated the account' },
        ],
    },

    // Security Triggers
    password_reset_requested: {
        category: 'Security',
        description: 'When a customer requests a password reset',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'reset_url', description: 'Password reset URL' },
            { name: 'ip_address', description: 'IP address of the request' },
            { name: 'user_agent', description: 'Browser/device information' },
        ],
    },
    password_reset_by_crm: {
        category: 'Security',
        description: 'When a CRM user resets a customer password',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'new_password', description: '⚠️ The new password (use with caution!)' },
            { name: 'reset_by', description: 'Name of CRM user who reset the password' },
            { name: 'reset_by_id', description: 'ID of CRM user who reset the password' },
            { name: 'ip_address', description: 'IP address' },
            { name: 'timestamp', description: 'When password was reset' },
            { name: 'user_name', description: 'Name of the CRM user who performed this action' },
            { name: 'user_email', description: 'Email of the CRM user who performed this action' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the CRM user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the CRM user' },
        ],
    },
    password_changed: {
        category: 'Security',
        description: 'When a customer changes their password (security alert)',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'changed_by', description: 'Who changed the password' },
            { name: 'ip_address', description: 'IP address' },
            { name: 'user_agent', description: 'Browser/device information' },
            { name: 'timestamp', description: 'When password was changed' },
        ],
    },
    login_attempt_failed: {
        category: 'Security',
        description: 'When a login attempt fails',
        variables: [
            { name: 'customer_id', description: 'Customer UUID (if account exists)' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'email', description: 'Email address used in login attempt' },
            { name: 'ip_address', description: 'IP address of the attempt' },
            { name: 'user_agent', description: 'Browser/device information' },
            { name: 'attempt_count', description: 'Number of failed attempts' },
        ],
    },
    suspicious_activity: {
        category: 'Security',
        description: 'When suspicious activity is detected',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'activity_type', description: 'Type of suspicious activity' },
            { name: 'risk_score', description: 'Risk score' },
            { name: 'details', description: 'Additional details' },
            { name: 'ip_address', description: 'IP address' },
        ],
    },

    // Compliance Triggers
    kyc_approval: {
        category: 'Compliance',
        description: 'When KYC verification is approved',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'account_number', description: 'Trading account number' },
            { name: 'action_date', description: 'Approval date' },
            { name: 'company_name', description: 'Company/system name' },
            { name: 'cta_url', description: 'Login URL' },
        ],
    },
    kyc_expiry: {
        category: 'Compliance',
        description: 'When KYC documents are expiring',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'document_type', description: 'Type of document expiring' },
            { name: 'expiry_date', description: 'When document expires' },
            { name: 'days_until_expiry', description: 'Days remaining until expiry' },
        ],
    },
    kyc_failed: {
        category: 'Compliance',
        description: 'When KYC verification fails',
        variables: [
            { name: 'customer_id', description: 'Customer UUID' },
            { name: 'customer_number', description: 'Customer readable number' },
            { name: 'customer_name', description: 'Customer full name' },
            { name: 'customer_email', description: 'Customer email address' },
            { name: 'old_status', description: 'Previous KYC status' },
            { name: 'new_status', description: 'New KYC status (rejected/failed)' },
            { name: 'verification_date', description: 'When KYC was reviewed' },
        ],
    },

    // User (CRM/Admin) Operation Triggers
    user_created: {
        category: 'User Operations',
        description: 'When a new CRM/Admin user is created',
        variables: [
            { name: 'user_id', description: 'User UUID' },
            { name: 'user_name', description: 'User full name' },
            { name: 'user_email', description: 'User email address' },
            { name: 'user_title', description: 'User job title' },
            { name: 'role_id', description: 'Role ID' },
            { name: 'role_name', description: 'Role name' },
            { name: 'created_by', description: 'Who created the user' },
            { name: 'created_by_id', description: 'ID of creator' },
            { name: 'timestamp', description: 'When user was created' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the created user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the created user' },
        ],
    },
    user_password_reset_requested: {
        category: 'User Operations',
        description: 'When a CRM/Admin user requests a password reset',
        variables: [
            { name: 'user_id', description: 'User UUID' },
            { name: 'user_name', description: 'User full name' },
            { name: 'user_email', description: 'User email address' },
            { name: 'reset_url', description: 'Password reset URL' },
            { name: 'ip_address', description: 'IP address' },
            { name: 'user_agent', description: 'Browser/device information' },
        ],
    },
    user_password_reset_by_admin: {
        category: 'User Operations',
        description: 'When an admin resets a user password',
        variables: [
            { name: 'user_id', description: 'User UUID' },
            { name: 'user_name', description: 'User full name' },
            { name: 'user_email', description: 'User email address' },
            { name: 'new_password', description: '⚠️ The new password (use with caution!)' },
            { name: 'reset_by', description: 'Name of admin who reset the password' },
            { name: 'reset_by_id', description: 'ID of admin' },
            { name: 'ip_address', description: 'IP address' },
            { name: 'timestamp', description: 'When password was reset' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the user' },
        ],
    },
    user_password_changed: {
        category: 'User Operations',
        description: 'When a CRM/Admin user changes their password',
        variables: [
            { name: 'user_id', description: 'User UUID' },
            { name: 'user_name', description: 'User full name' },
            { name: 'user_email', description: 'User email address' },
            { name: 'changed_by', description: 'Who changed the password' },
            { name: 'changed_by_id', description: 'ID of who changed the password' },
            { name: 'ip_address', description: 'IP address' },
            { name: 'user_agent', description: 'Browser/device information' },
            { name: 'timestamp', description: 'When password was changed' },
        ],
    },
    user_activated: {
        category: 'User Operations',
        description: 'When a CRM/Admin user account is activated',
        variables: [
            { name: 'user_id', description: 'User UUID' },
            { name: 'user_name', description: 'User full name' },
            { name: 'user_email', description: 'User email address' },
            { name: 'old_status', description: 'Previous status' },
            { name: 'new_status', description: 'New status (active)' },
            { name: 'changed_by', description: 'Who activated the user' },
            { name: 'changed_by_id', description: 'ID of who activated the user' },
            { name: 'timestamp', description: 'When user was activated' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the user' },
        ],
    },
    user_deactivated: {
        category: 'User Operations',
        description: 'When a CRM/Admin user account is deactivated',
        variables: [
            { name: 'user_id', description: 'User UUID' },
            { name: 'user_name', description: 'User full name' },
            { name: 'user_email', description: 'User email address' },
            { name: 'old_status', description: 'Previous status' },
            { name: 'new_status', description: 'New status (inactive)' },
            { name: 'changed_by', description: 'Who deactivated the user' },
            { name: 'changed_by_id', description: 'ID of who deactivated the user' },
            { name: 'timestamp', description: 'When user was deactivated' },
            { name: 'user_whatsapp_number', description: 'WhatsApp number of the user' },
            { name: 'user_telegram', description: 'Telegram handle/number of the user' },
        ],
    },

    // System Triggers
    system_maintenance: {
        category: 'System',
        description: 'When system maintenance is scheduled',
        variables: [
            { name: 'maintenance_start', description: 'Maintenance start time' },
            { name: 'maintenance_end', description: 'Maintenance end time' },
            { name: 'affected_services', description: 'Services that will be affected' },
            { name: 'message', description: 'Additional message about the maintenance' },
        ],
    },
};

/**
 * Get variables for a specific trigger, including general variables
 */
export function getTriggerVariables(trigger: string): TriggerVariable[] {
    const triggerInfo = TRIGGER_VARIABLES[trigger];
    if (!triggerInfo) {
        return [...GENERAL_VARIABLES];
    }
    return [...GENERAL_VARIABLES, ...triggerInfo.variables];
}

/**
 * Get trigger info (category, description, variables)
 */
export function getTriggerInfo(trigger: string): TriggerInfo | null {
    return TRIGGER_VARIABLES[trigger] || null;
}

/**
 * Get all available triggers grouped by category
 */
export function getTriggersByCategory(): Record<string, string[]> {
    const categories: Record<string, string[]> = {};

    Object.entries(TRIGGER_VARIABLES).forEach(([trigger, info]) => {
        if (!categories[info.category]) {
            categories[info.category] = [];
        }
        categories[info.category].push(trigger);
    });

    return categories;
}
